import { Button, IconButton, Tooltip } from '@mui/material';
import styles from './ActionButtons.module.scss';
import AISummaryIcon from '../../../../../assets/svg/list_alt_check.svg?react';
import FileSaveIcon from '../../../../../assets/svg/file_save.svg?react';
import EditContractIcon from '../../../../../assets/svg/contract_edit.svg?react';
import BrushIcon from '../../../../../assets/svg/brush.svg?react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonsProps } from './types';
import { DocumentsEditButton } from '../../../../common/document-edit-button/DocumentsEditButton';
import { useSearchParams } from 'react-router-dom';
import { useHasClerkRole } from '../../../../common/hooks/useHasClerkRole';
import { useCheckPermission } from '../../../../common/hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../../../../common/constants/permissions';

export const ActionButtons = ({
  handleAISummaryGeneration,
  isImage,
  handleDocumentDownload,
  handleSignDocument,
  handleEditDocument,
  handleCreateTemplate,
  handleDeleteDocument,
  handleSaveDocument,
  buttonSaveDisabled,
  handleCreateTemplateMainBtn,
  doc,
  creatingTemplateEnabled,
  isDocumentContentTab,
  setEditingDocument,
}: ActionButtonsProps) => {
  const { t } = useTranslation();
  const checkPermission = useCheckPermission();
  const [params] = useSearchParams();
  const userIsEmployee = useHasClerkRole('org:employee');
  const contractGenerated = params.get('contractGenerated');
  const editDocumentParam = params.get('editDocument');
  const checkDocumentEditingPermission = () => {
    switch (doc?.category) {
      case 'employment':
        return checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE_DOCUMENT);
      case 'business':
        return checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_UPDATE_DOCUMENT);
      case 'customers':
        return checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE_DOCUMENT);
      case 'suppliers':
        return checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE_DOCUMENT);
    }
  };
  const checkDocumentDeletingPermission = () => {
    switch (doc?.category) {
      case 'employment':
        return checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_DELETE_DOCUMENT);
      case 'business':
        return checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_DELETE_DOCUMENT);
      case 'customers':
        return checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_DELETE_DOCUMENT);
      case 'suppliers':
        return checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_DELETE_DOCUMENT);
    }
  };
  const checkDocUploadOrContractDeletingPermission = () => {
    if ('contract_generation' in doc) {
      return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_DELETE);
    } else {
      return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_DELETE);
    }
  };
  const checkDocUploadOrContractEditingPermission = () => {
    if ('contract_generation' in doc) {
      return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_EDIT);
    } else {
      return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_EDIT);
    }
  };
  return (
    <>
      {handleCreateTemplateMainBtn &&
        !creatingTemplateEnabled &&
        handleCreateTemplate &&
        !isImage && (
          <>
            {userIsEmployee || !checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_CREATE) ? (
              <Tooltip title={t('messages.contactAdministrator')} placement='top' arrow>
                <Button
                  type='button'
                  className='btn-default btn-action-basic btn-small'
                  role='button'
                  variant='contained'
                  onClick={null}
                  disabled={true}
                  style={{ marginRight: '8px' }}
                >
                  {t('buttons.createTemplate')}
                </Button>
              </Tooltip>
            ) : (
              <Button
                type='button'
                className='btn-action-basic btn-primary-action btn-small'
                role='button'
                variant='contained'
                disabled={!buttonSaveDisabled}
                onClick={handleCreateTemplate}
                style={{ marginRight: '8px' }}
              >
                {t('buttons.createTemplate')}
              </Button>
            )}
          </>
        )}
      {Boolean(handleSaveDocument) && !buttonSaveDisabled && isDocumentContentTab && (
        <Button
          type='button'
          className='btn-default btn-action-basic btn-cancel btn-small'
          role='button'
          variant='contained'
          disabled={buttonSaveDisabled}
          onClick={() => setEditingDocument(false)}
          style={{ marginRight: '8px' }}
        >
          {t('buttons.cancel')}
        </Button>
      )}
      {Boolean(handleSaveDocument) && !buttonSaveDisabled && isDocumentContentTab && (
        <Button
          type='button'
          className='btn-default btn-action-basic btn-small'
          role='button'
          variant='contained'
          disabled={buttonSaveDisabled}
          onClick={handleSaveDocument}
          style={{ marginRight: '8px' }}
        >
          {t('buttons.saveContent')}
        </Button>
      )}
      {(!isDocumentContentTab || buttonSaveDisabled) &&
        !isImage &&
        !doc?.is_flattened &&
        checkDocumentEditingPermission() && (
          <Tooltip
            title={
              userIsEmployee ||
              !checkDocumentEditingPermission() ||
              !checkDocUploadOrContractEditingPermission()
                ? t('messages.contactAdministrator')
                : t('buttons.edit')
            }
            placement='top'
            arrow
          >
            <IconButton
              size='small'
              className={styles.actionButton}
              disabled={
                userIsEmployee ||
                !checkDocumentEditingPermission() ||
                !checkDocUploadOrContractEditingPermission()
              }
              onClick={
                userIsEmployee ||
                !checkDocumentEditingPermission() ||
                !checkDocUploadOrContractEditingPermission()
                  ? null
                  : handleEditDocument
              }
            >
              <BrushIcon />
            </IconButton>
          </Tooltip>
        )}
      {handleSignDocument && !isImage && !doc?.is_flattened && (
        <Tooltip
          title={
            userIsEmployee || checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_SIGNING_CREATE)
              ? t('messages.contactAdministrator')
              : t('buttons.sign')
          }
          placement='top'
          arrow
        >
          <IconButton
            size='small'
            disabled={
              userIsEmployee || checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_SIGNING_CREATE)
            }
            className={styles.actionButton}
            onClick={handleSignDocument}
          >
            <EditContractIcon />
          </IconButton>
        </Tooltip>
      )}
      {!isImage && (
        <Tooltip
          title={
            checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_EXPORT)
              ? t('buttons.download')
              : t('messages.contactAdministrator')
          }
          placement='top'
          arrow
        >
          <IconButton
            size='small'
            className={styles.actionButton}
            disabled={!checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_EXPORT)}
            onClick={handleDocumentDownload}
          >
            <FileSaveIcon />
          </IconButton>
        </Tooltip>
      )}
      {!isImage && (
        <Tooltip
          title={
            checkPermission(USER_PERMISSIONS.ORG_AI_SUMMARY_CREATE)
              ? t('buttons.summary')
              : t('messages.contactAdministrator')
          }
          placement='top'
          arrow
        >
          <IconButton
            size='small'
            className={styles.actionButton}
            onClick={handleAISummaryGeneration}
            disabled={!checkPermission(USER_PERMISSIONS.ORG_AI_SUMMARY_CREATE)}
          >
            <AISummaryIcon />
          </IconButton>
        </Tooltip>
      )}
      {!isImage &&
        !doc?.is_flattened &&
        editDocumentParam &&
        ((!contractGenerated && checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_CREATE)) ||
          checkDocumentDeletingPermission()) && (
          <DocumentsEditButton
            document={doc}
            createTemplateFn={
              !contractGenerated && checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_TEMPLATE_CREATE)
                ? handleCreateTemplate
                : null
            }
            deleteDocumentFn={
              checkDocumentDeletingPermission() && checkDocUploadOrContractDeletingPermission()
                ? handleDeleteDocument
                : null
            }
          />
        )}
    </>
  );
};
